import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_INDICATORS_OF_RISK_GENERAL } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, PhasrCategories, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import {
  GzPolicyPanelAccordionTemplateWebComponent,
} from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import {
  getRepeatIntervalFormOptions,
  getWeekDaysFormOptions,
  WeekDaysValues,
  WeekDayValueToScheduleFieldName,
} from '../../../../../../common/policyPanel/uiSettings/schedulerTemplate/types';
import {
  IndicatorsOfRiskFormValue,
  IndicatorsOfRiskSearchableKeywords,
} from '../../../../utils/uiSettings/indicatorsOfRisk/indicators-of-risk.types';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import moment from 'moment/moment';
import deepmerge from 'deepmerge';
import { getSupportLinkForPlatform } from '../../../../utils/links';
import { GzDatePickerWebComponent } from '../../../../../../../ui/gzDatePicker';
import { GzTimePickerWebComponent } from '../../../../../../../ui/gzTimePicker';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { GzInputNumberWebComponent } from '../../../../../../../ui/gzInput';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzMultiSelectGroupWebComponent } from '../../../../../../../ui/gzMultiSelect';
import { FontSizes, FontWeights, LabelWidthValues } from '../../../../../../../types/common/types';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import {
  getIndicatorsOfRiskDefaultValue,
  getIndicatorsOfRiskSearchableKeywords,
  getPhasrDefaultValue,
} from '../../../../utils/uiSettings/indicatorsOfRisk/indicators-of-risk.values';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { RecurrenceType, RepeatInterval } from '@nirvana/shared/dtos/types/policies/common';
import { Feature } from '@nirvana/shared/constants';
import { classMap } from 'lit/directives/class-map.js';
import { getPhasrOptions, PhasrOptions } from '../types';
import { dropdownWidth } from '../../../../../../../ui/gzComplexSearch/types';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { buildUrl } from '@nirvana/shared/navigation';
import { Target } from '../../../../../../../ui/gzLinkComponent/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_INDICATORS_OF_RISK_GENERAL)
export class GzIndicatorsOfRiskGeneralWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  IndicatorsOfRiskFormValue,
  IndicatorsOfRiskSearchableKeywords
> {
  /**
   * Path for Risk Management module. (Section is missing)
   */
  protected path = {
    moduleId: ModuleId.INDICATORS_OF_RISK,
    sectionId: SectionId.NONE,
  };

  protected phasrPath = {
    moduleId: ModuleId.PHASR,
    sectionId: SectionId.NONE,
  };

  private hasPhasr = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_PHASR_VISIBLE]);

  /**
   * Searchable keywords for Risk Management section.
   */
  protected searchableKeywords = getIndicatorsOfRiskSearchableKeywords();

  /**
   * Options for Recurrence section.
   */
  private repeatIntervalOptions = [RepeatInterval.DAYS, RepeatInterval.WEEKDAYS];

  @query('#indicators-of-risk-toggle')
  private indicatorsOfRiskToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#indicators-of-risk-info-text')
  private indicatorsOfRiskInfoText: HTMLElement;

  @query('#indicators-of-risk-content')
  private indicatorsOfRiskContent: HTMLElement;

  @query('#indicators-of-risk-scheduler-mode')
  private indicatorsOfRiskRecoveryMode: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#indicators-of-risk-nsva-exclusions')
  private indicatorsOfRiskNsvaExclusions: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#policy-panel-scheduler-start-date-form-field')
  private startDateFormField: GzFormFieldWebComponent<GzDatePickerWebComponent, any>;

  @query('#policy-panel-scheduler-start-time-form-field')
  private startTimeFormField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;

  @query('#policy-panel-recurrence-type')
  private recurrenceTypeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('#policy-panel-recurrence-run-every')
  private runEveryFormField: GzFormFieldWebComponent<GzInputNumberWebComponent>;

  @query('#policy-panel-recurrence-run-per')
  private runPerFormField: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#policy-panel-recurrence-weekdays-run-every')
  private weekDaysFormField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent>;

  @query('#phasr-content')
  private phasrContent: HTMLElement;

  @query('#phasr-section')
  private phasrSection: HTMLElement;

  @query('#phasr-toggle')
  private phasrToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#phasr-tampering-tools')
  private tamperingTools: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#phasr-lolbins')
  private lolbins: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#phasr-miners')
  private miners: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#phasr-hack-tools')
  private hackTools: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#phasr-remote-access-tools')
  private remoteAccessTools: GzFormFieldWebComponent<GzDropdownWebComponent>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`
      <gz-form-field id="indicators-of-risk-toggle" status-field>
        <gz-toggle
          class="content"
          searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RiskManagement].label}"
          gz-form-input
          @toggleChange="${this.onToggleChange}"
        ><span
        >${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RiskManagement].translatedLabel}</span
        ></gz-toggle
        >
      </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html`
      <gz-text id="indicators-of-risk-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Enables recurrent risk scanning on target endpoints. This means you can schedule scanning for security risks, such as Automatic Updates or User Access Control settings.',
      )}
      </gz-text>
      <br />
      <gz-link
        id="indicators-of-risk-info-link"
        iconsize="20"
        type="underline"
        color="secondary"
        href="${getSupportLinkForPlatform(this.path, ['fullDescription'])}"
        fontsize="12"
      >
        <span>${translateFn('Read the full description of indicators of risk and the available fix actions.')}</span>
      </gz-link>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    const classes = classMap({
      hidden: !this.hasPhasr,
    });

    const isPartner = Application.Main.getActiveCompany().isPartner;
    const hasSensorsIntegrationsVisible = Application.Main.hasFeatures([
      Feature.PRODUCT_FEATURE_SENSORS_INTEGRATIONS_VISIBLE,
    ]);
    const hasNetworkSensors = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_NETWORK_SENSORS]);

    const nsvaExclusionsClasses = classMap({
      hidden: !hasSensorsIntegrationsVisible || (!isPartner && !hasNetworkSensors),
    });

    const repeatIntervalFormOptions = this.repeatIntervalOptions.map(
      (repeatInterval: RepeatInterval) => getRepeatIntervalFormOptions()[repeatInterval],
    );
    const weekDaysFormOptions = getWeekDaysFormOptions();

    const phasrOptions = getPhasrOptions();
    const sensorsManagementLink = buildUrl('/configuration/sensors');
    return html`
      <div id="indicators-of-risk-content">
        <div class="form-section" id="indicators-of-risk-scheduler">
          <gz-title
            searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RiskScheduler].label}"
            fontSize="11"
            fontWeight="600"
            class="form-subtitle"
            id="policy-panel-scheduler-subtitle"
          >${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RiskScheduler].translatedLabel}
          </gz-title>

          <div id="policy-panel-scheduler-start-date-and-time-form-field">
            <gz-form-field
              id="policy-panel-scheduler-start-date-form-field"
              class="label-mw-250"
              label="${translateFn('Start date and time')}"
            >
              <gz-date-picker
                gz-form-input
                placeholder="day/month/year"
                id="policy-panel-scheduler-start-date"
                labelWidth="${LabelWidthValues.MEDIUM}"
                navigateToSelected="True"
              ></gz-date-picker>
              <gz-input-error></gz-input-error>
            </gz-form-field>

            <gz-form-field id="policy-panel-scheduler-start-time-form-field" class="label-mw-250">
              <gz-time-picker gz-form-input id="policy-panel-scheduler-start-time"></gz-time-picker>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>

          <div id="policy-panel-recurrence">
            <gz-text
              searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RiskRecurrence].label}"
              id="policy-panel-recurrence-title"
              class="form-field-label mb-2"
            >${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RiskRecurrence].translatedLabel}
            </gz-text>

            <gz-form-field class="pl-5" id="policy-panel-recurrence-type">
              <gz-radio-group
                gz-form-input
                class="d-flex flex-column"
                @radioSelectionChanged=${this.onRadioSelectionChanged}
              >
                <div class="radio" id="policy-panel-recurrence-schedule">
                  <gz-radio
                    id="policy-panel-recurrence-schedule-radio"
                    label="${translateFn('Schedule task to run once every') + ':'}"
                    .value=${RecurrenceType.SCHEDULE}
                    wrapped
                  ></gz-radio>

                  <gz-form-field id="policy-panel-recurrence-run-every">
                    <gz-input-number gz-form-input min="1"></gz-input-number>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>
                  <gz-form-field id="policy-panel-recurrence-run-per">
                    <gz-dropdown gz-form-input options="${JSON.stringify(repeatIntervalFormOptions)}"></gz-dropdown>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>
                </div>

                <div class="radio" id="policy-panel-recurrence-weekdays">
                  <gz-radio
                    id="policy-panel-recurrence-weekdays-radio"
                    label="${translateFn('Run task every') + ':'}"
                    .value=${RecurrenceType.WEEKDAYS}
                  ></gz-radio>

                  <gz-form-field id="policy-panel-recurrence-weekdays-run-every">
                    <gz-multiselect-group gz-form-input .options=${weekDaysFormOptions}></gz-multiselect-group>
                    <gz-input-error></gz-input-error>
                  </gz-form-field>
                </div>
              </gz-radio-group>
            </gz-form-field>
          </div>

          <gz-form-field id="indicators-of-risk-scheduler-mode" class="mb-1">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RiskRunAsSoonAsPossible]
                .label}"
              gz-form-input
              label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RiskRunAsSoonAsPossible]
                .translatedLabel}"
            ></gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-form-field id="indicators-of-risk-nsva-exclusions" class="${nsvaExclusionsClasses}">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.NSVAExclusions].label}"
              gz-form-input
              label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.NSVAExclusions].translatedLabel}"
            >
            </gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-text id="indicators-of-risk-info-text" fontSize="11" fontWeight="400"
                   class="infoText ${nsvaExclusionsClasses}">
            ${translateFn(
              'This will prevent BEST from blocking and reporting alerts and incidents when the endpoint is being scanned by the Network Vulnerability Scanner.',
            )}
          </gz-text>
          <gz-text fontSize="11" fontWeight="400" class="infoText ${nsvaExclusionsClasses}">
            ${
              unsafeHTML(translateFn(
                'You can find these IPs in the {HTML_TAG_START} Sensors Management {HTML_TAG_STOP} section.',
                {
                  HTML_TAG_START: `<gz-link
                      type="underline"
                      href="${sensorsManagementLink}"
                      target="${Target.BLANK}"
                    >`,
                  HTML_TAG_STOP: '</gz-link>',
                },
              ))}
          </gz-text>
        </div>
      </div>
      <div class="form-section ${classes}" id="phasr-section">
        <div class="title-header">
          <div class="module-name">
            <gz-form-field id="phasr-toggle">
              <gz-toggle
                class="content"
                searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.Phasr].label}"
                gz-form-input
                @toggleChange="${this.onPhasrToggleChange}"
              ><span
              >${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.Phasr].translatedLabel}</span
              ></gz-toggle
              >
            </gz-form-field>
          </div>
          <div class="module-link">
            <gz-link
              class="support-link hidden"
              iconsize="20"
              type="underline"
              color="secondary"
              href="${getSupportLinkForPlatform(this.phasrPath, ['support'])}"
              fontsize="12"
            >
              <span>${translateFn('Get help from Support Center')}</span>
            </gz-link>
          </div>
        </div>
        <div id="phasr-content">
          <gz-text
            id="phasr-info-text"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
          >${translateFn(
            'When enabled, PHASR continuously monitors endpoints and analyzes user behavior patterns to identify potential attack vectors. PHASR provides a comprehensive overview of your company\'s internal attack surface and offers actionable recommendations to reduce it',
          )}
          </gz-text>
          <gz-text
            fontSize="${FontSizes.PARAGRAPH_11}"
            fontWeight="${FontWeights.SEMIBOLD}"
            class="form-subtitle"
            id="policy-panel-phasr-configure"
          >${translateFn('Configure PHASR monitoring for the following activity types:')}
          </gz-text>
          <gz-form-field
            id="phasr-lolbins"
            class="mt-2"
            searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.LOLBins].label}"
            label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.LOLBins].translatedLabel}"
          >
            <gz-dropdown
              gz-form-input
              class="small-dropdown"
              labelWidth='${dropdownWidth.MEDIUM}'
              options="${JSON.stringify(phasrOptions)}"
              .value=${PhasrOptions.OFF}
            >
            </gz-dropdown>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="phasr-remote-access-tools"
            class="mb-1"
            searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RemoteAdminTools].label}"
            label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.RemoteAdminTools].translatedLabel}"
          >
            <gz-dropdown
              gz-form-input
              class="small-dropdown"
              labelWidth='${dropdownWidth.MEDIUM}'
              options="${JSON.stringify(phasrOptions)}"
              .value=${PhasrOptions.OFF}
            >
            </gz-dropdown>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="phasr-tampering-tools"
            class="mb-1"
            searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.TamperingTools].label}"
            label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.TamperingTools].translatedLabel}"
          >
            <gz-dropdown
              gz-form-input
              class="small-dropdown"
              labelWidth='${dropdownWidth.MEDIUM}'
              options="${JSON.stringify(phasrOptions)}"
              .value=${PhasrOptions.OFF}
            >
            </gz-dropdown>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="phasr-hack-tools"
            class="mb-1"
            searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.HackTools].label}"
            label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.HackTools].translatedLabel}"
          >
            <gz-dropdown
              gz-form-input
              class="small-dropdown"
              labelWidth='${dropdownWidth.MEDIUM}'
              options="${JSON.stringify(phasrOptions)}"
              .value=${PhasrOptions.OFF}
            >
            </gz-dropdown>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="phasr-miners"
            class="mb-1"
            searchable-label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.Miners].label}"
            label="${this.searchableKeywords[IndicatorsOfRiskSearchableKeywords.Miners].translatedLabel}"
          >
            <gz-dropdown
              gz-form-input
              class="small-dropdown"
              labelWidth='${dropdownWidth.MEDIUM}'
              options="${JSON.stringify(phasrOptions)}"
              .value=${PhasrOptions.OFF}
            >
            </gz-dropdown>
            <gz-input-error></gz-input-error>
          </gz-form-field>
          <gz-info-box severity='warning' id='phasr-switching-warning'>
            ${translateFn('Switching between monitoring modes will remove PHASR restrictions currently applied to behavioral profiles.')}
          </gz-info-box>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.indicatorsOfRisk
   */
  protected getValueFromManager(): IndicatorsOfRiskFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.INDICATORS_OF_RISK]: getIndicatorsOfRiskDefaultValue(),
        [ModuleId.PHASR]: getPhasrDefaultValue(),
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.INDICATORS_OF_RISK]: PolicyPanelManager.getInstance().getPolicy('uiSettings.indicatorsOfRisk'),
        [ModuleId.PHASR]: PolicyPanelManager.getInstance().getPolicy('uiSettings.phasr'),
      },
    };

    // convert from PhasrCategory to PhasrOptions format
    const phasrCategories = value.uiSettings[ModuleId.PHASR].categories;
    value.uiSettings[ModuleId.PHASR].categories = this.convertPhasrCategoryToOptions(phasrCategories);

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        indicatorsOfRisk: {
          enabled: this.indicatorsOfRiskToggle,
          scheduler: {
            recurrenceType: this.recurrenceTypeFormField,
            runEvery: this.runEveryFormField,
            runPer: this.runPerFormField,
            weekdays: this.weekDaysFormField,
            startDate: {
              date: this.startDateFormField,
              time: this.startTimeFormField,
            },
            recovery: {
              mode: {
                runImmediately: this.indicatorsOfRiskRecoveryMode,
              },
            },
          },
          nsvaExclusions: this.indicatorsOfRiskNsvaExclusions,
        },
        phasr: {
          enabled: this.phasrToggle,
          categories: {
            lolbins: this.lolbins,
            miners: this.miners,
            hackTools: this.hackTools,
            remoteAccessTools: this.remoteAccessTools,
            tamperingTools: this.tamperingTools,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    const scheduler = this.valueT.uiSettings.indicatorsOfRisk.scheduler;
    const startDate = new Date(scheduler.startDate.date).getTime() / 1000;
    const startTime = {
      hours: scheduler.startDate.hours,
      minutes: scheduler.startDate.minutes,
    };
    const weekDays = WeekDaysValues.filter(x => scheduler[WeekDayValueToScheduleFieldName[x]]);

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        indicatorsOfRisk: {
          enabled: this.valueT.uiSettings.indicatorsOfRisk.enabled,
          scheduler: {
            recurrenceType: scheduler.recurrenceType,
            runEvery: scheduler.runEvery,
            runPer: scheduler.runPer,
            weekdays: weekDays,
            startDate: {
              date: startDate,
              time: startTime,
            },
            recovery: scheduler.recovery,
          },
          nsvaExclusions: this.valueT.uiSettings.indicatorsOfRisk.nsvaExclusions != null ? this.valueT.uiSettings.indicatorsOfRisk.nsvaExclusions : true,
        },
        phasr: {
          enabled: this.valueT.uiSettings.phasr.enabled,
          categories: {
            lolbins: this.valueT.uiSettings.phasr.categories.lolbins,
            miners: this.valueT.uiSettings.phasr.categories.miners,
            hackTools: this.valueT.uiSettings.phasr.categories.hackTools,
            remoteAccessTools: this.valueT.uiSettings.phasr.categories.remoteAccessTools,
            tamperingTools: this.valueT.uiSettings.phasr.categories.tamperingTools,
          },
        },
      },
    };
  }

  /**
   * Set validators for form fields.
   */
  protected setValidators() {
    this.weekDaysFormField.Validators = [RequiredValidator(translateFn('Select at least one day of the week.'))];
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);
    const schedulerPath = ['uiSettings', 'indicatorsOfRisk', 'scheduler'];

    // compute startDate field from form value
    const startDateTime = _.get(value, [...schedulerPath, 'startDate']);
    _.set(
      value,
      [...schedulerPath, 'startDate', 'date'],
      moment(new Date(startDateTime['date'] * 1000)).format('MM/DD/YYYY'),
    );
    _.set(value, [...schedulerPath, 'startDate', 'hours'], startDateTime['time']['hours']);
    _.set(value, [...schedulerPath, 'startDate', 'minutes'], startDateTime['time']['minutes']);

    // compute week days fields from form value
    const weekdays = this.weekDaysFormField.disabled
      ? this.weekDaysFormField.value
      : _.get(value, [...schedulerPath, 'weekdays']);
    WeekDaysValues.forEach(day =>
      _.set(value, [...schedulerPath, WeekDayValueToScheduleFieldName[day]], weekdays.includes(day) ? 1 : 0),
    );

    // set run every value form if disabled
    if (this.runEveryFormField.disabled) {
      _.set(value, [...schedulerPath, 'runEvery'], this.runEveryFormField.value);
    }

    // set run per value form if disabled
    if (this.runPerFormField.disabled) {
      _.set(value, [...schedulerPath, 'runPer'], this.runPerFormField.value);
    }

    // unset temporary fields from form group
    _.unset(value, [...schedulerPath, 'startDate', 'time']);
    _.unset(value, [...schedulerPath, 'weekdays']);

    // convert from PhasrOptions to PhasrCategory format
    const phasrCategories = _.get(value, ['uiSettings', 'phasr', 'categories']);
    _.set(value, ['uiSettings', 'phasr', 'categories'], this.convertPhasrOptionsToCategory(phasrCategories));

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings'], {}) as FormGroupValue;
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.indicatorsOfRiskInfoText, !event.detail.checked);
    toggleDisabled(this.indicatorsOfRiskContent, !event.detail.checked);
    toggleDisabled(this.phasrSection, !event.detail.checked);
  }

  /**
   * Enable/Disable Phasr module.
   * @param event
   */
  onPhasrToggleChange(event: CustomEvent) {
    toggleDisabled(this.phasrContent, !event.detail.checked);
  }

  /**
   * On radio selection change, disable unselected radio option.
   * @param event
   */
  private onRadioSelectionChanged(event: CustomEvent) {
    const isScheduleType = event.detail.selectedValue === RecurrenceType.SCHEDULE;
    this.disableRecurrenceFields(!isScheduleType, isScheduleType);
  }

  /**
   * Enable/disable form fields based on received parameters.
   * @param isScheduleDisabled
   * @param isWeekdaysDisabled
   */
  private disableRecurrenceFields(isScheduleDisabled: boolean, isWeekdaysDisabled: boolean) {
    this.runEveryFormField.disabled = isScheduleDisabled;
    this.runPerFormField.disabled = isScheduleDisabled;
    this.weekDaysFormField.disabled = isWeekdaysDisabled;

    if (isWeekdaysDisabled) {
      this.weekDaysFormField.resetField();
    } else {
      this.weekDaysFormField.validate(true);
    }
  }

  /**
   * Convert PhasrOptions to PhasrCategory format.
   * @param {{[key: string]: PhasrOptions}} phasrOptions
   * @returns {PhasrCategories}
   * @private
   */
  private convertPhasrOptionsToCategory(phasrOptions: { [key: string]: PhasrOptions }): PhasrCategories {
    const phasrCategories = getPhasrDefaultValue()['categories'];
    Object.keys(phasrOptions).forEach(category => {
      const categoryValue = phasrOptions[category];
      if (categoryValue === PhasrOptions.OFF) {
        phasrCategories[category] = {
          enabled: false,
          autopilot: false,
        };
      } else {
        phasrCategories[category] = {
          enabled: true,
          autopilot: categoryValue === PhasrOptions.AUTOPILOT,
        };
      }
    });
    return phasrCategories;
  }

  /**
   * Convert PhasrCategory to PhasrOptions format.
   * @param {PhasrCategories} phasrCategories
   * @returns {{[key: string]: PhasrOptions}}
   * @private
   */
  private convertPhasrCategoryToOptions(phasrCategories: PhasrCategories): { [key: string]: PhasrOptions } {
    const phasrOptions = {};
    Object.keys(phasrCategories).forEach(category => {
      const categoryValue = phasrCategories[category];
      if (!categoryValue.enabled) {
        phasrOptions[category] = PhasrOptions.OFF;
      } else {
        phasrOptions[category] = categoryValue.autopilot ? PhasrOptions.AUTOPILOT : PhasrOptions.DIRECT_CONTROL;
      }
    });
    return phasrOptions;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_INDICATORS_OF_RISK_GENERAL: GzIndicatorsOfRiskGeneralWebComponent;
  }
}
